import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Container } from 'reactstrap';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  LinearProgress,
  createTheme,
  ThemeProvider
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CustomerDetails from './components/CustomerDeliveryTagDetails';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import { showNotification, successNofication } from 'src/shared/components/Notification';
import { ERROR_MESSAGES } from 'src/utils/pnd/PndConstants';
import AddOrUpdateTagModal from '../AddOrUpdate/AddOrUpdateTagModal';
import ConfirmationDialog from 'src/shared/components/ConfirmationDialog';

const theme = createTheme({
  typography: {
    fontFamily: 'ProductSansLight, sans-serif',
    h3: {
      fontFamily: 'ProductSansBold, sans-serif',
      fontSize: 28,
      fontWeight: 600,
      color: '#1e293b',
    },
    subtitle1: {
      color: '#64748b',
      fontSize: 14,
    }
  },
  palette: {
    primary: { main: '#0ea5e9' },
    secondary: { main: '#64748b' },
    error: { main: '#ef4444' }
  }
});

const CustomerTagIndex = () => {
  const [reactTableData, setReactTableData] = useState({ tableHeaderData: [], tableRowsData: [] });
  const [temp, setTempData] = useState(null);
  const [viewDetailPopup, setViewDetailPopup] = useState(false);
  const [isTitle, setTitle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isNoRecordFound, setNoRecordFound] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isDelete, setDelete] = useState(null);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);

  const getAllCustomers = useCallback(async (currentPage = page, currentSize = pageSize) => {
    try {
      setLoading(true);
      const response = await PndRestApi.GetCustomerTagMapDetails({ page: currentPage, size: currentSize });
      if (response.status === 200 && response.data.status === 200) {
        // Correctly extract the paginated content
        const { content, totalElements: total } = response.data.data;
        setReactTableData({ tableHeaderData: columns, tableRowsData: content || [] });
        setTotalElements(total || 0);
        setNoRecordFound(false);
      } else if (response?.data?.status === 204) {
        setNoRecordFound(true);
        setReactTableData({ tableHeaderData: columns, tableRowsData: [] });
        setTotalElements(0);
      } else {
        showNotification("theme-light", "ltr", `Error: ${response.data.status}`, `Message: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showNotification("theme-light", "ltr", `Error: 500`, ERROR_MESSAGES.tryCatchError);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    getAllCustomers();
  }, [getAllCustomers]);

  const handleDelete = (event) => {
    if (event?.custTagMapId) {
      setShowConfirmation(true);
      setDelete(event?.custTagMapId);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await PndRestApi.DeleteCustomerTagMapping(isDelete);
      if (response.status === 200 && response.data.status === 200) {
        successNofication("theme-light", "ltr", `Success`, `Message: Customer Tag Mapping Deleted Successfully`);
        getAllCustomers();
      } else {
        showNotification("theme-light", "ltr", `Error: ${response.data.status}`, `Message: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error deleting data:', error);
      showNotification("theme-light", "ltr", `Error: 500`, `Message: Operation Failed. Please try again`);
    } finally {
      setShowConfirmation(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: '#',
        accessor: (row, i) => i + 1,
        disableGlobalFilter: true,
        disableSortBy: true,
        width: 60,
        Cell: ({ row }) => (
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b' }}>
            {page * pageSize + row.index + 1}
          </Typography>
        ),
      },
      {
        Header: 'Customer ID',
        accessor: 'customerId',
        width: 180,
        Cell: ({ value }) => (
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b' }}>
            {value}
          </Typography>
        ),
      },
      {
        Header: "Delivery Tag",
        accessor: "tagName",
        width: 220,
        Cell: ({ row }) => (
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: '20px',
              backgroundColor: `${row.original.tagColorCode}15`,
              border: `1px solid ${row.original.tagColorCode}40`,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: row.original.tagColorCode
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: row.original.tagColorCode,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              {row.original?.tagName}
            </Typography>
          </Box>
        ),
      },
      {
        Header: 'Remarks',
        accessor: 'remarks',
        width: 350,
        Cell: ({ value }) => (
          <Typography variant="body2" sx={{ color: '#64748b', fontStyle: value ? 'normal' : 'italic' }}>
            {value || 'No remarks'}
          </Typography>
        ),
      },
      {
        Header: 'Action',
        accessor: 'edit',
        disableGlobalFilter: true,
        width: 120,
        Cell: row => (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Edit Details" arrow>
              <IconButton
                size="small"
                onClick={() => edit(row.row.original)}
                sx={{ color: '#0ea5e9', bgcolor: '#e0f2fe', '&:hover': { bgcolor: '#bae6fd' } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Mapping" arrow>
              <IconButton
                size="small"
                onClick={() => handleDelete(row.row.original)}
                sx={{ color: '#ef4444', bgcolor: '#fee2e2', '&:hover': { bgcolor: '#fecaca' } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [page, pageSize],
  );

  const edit = (event) => {
    if (event?.custTagMapId) {
      setTempData(event);
      setViewDetailPopup(true);
      setTitle("Edit Customer Tag Mapping")
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
        <Container fluid>
          <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                color: '#fff',
                display: 'flex'
              }}
            >
              <LocalOfferIcon fontSize="large" />
            </Box>
            <div>
              <Typography variant="h3">Customer Delivery Tags</Typography>
              <Typography variant="subtitle1">Manage and assign identification tags to your customers for better tracking.</Typography>
            </div>
          </Box>

          {loading && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}

          <Box sx={{ position: 'relative' }}>
            <CustomerDetails
              reactTableData={reactTableData}
              isNoRecordFound={isNoRecordFound}
              totalElements={totalElements}
              page={page}
              pageSize={pageSize}
              onPageChange={(newPage) => setPage(newPage)}
              onPageSizeChange={(newSize) => setPageSize(newSize)}
            />
          </Box>

          {viewDetailPopup && (
            <AddOrUpdateTagModal
              modalShow={viewDetailPopup}
              setModalShow={setViewDetailPopup}
              data={temp}
              isTitle={isTitle}
            />
          )}

          {showConfirmation && (
            <ConfirmationDialog
              open={showConfirmation}
              onClose={() => setShowConfirmation(false)}
              onConfirm={handleConfirmDelete}
              title="Confirm Deletion"
              message="Are you sure you want to permanently delete this customer tag mapping? This action cannot be undone."
            />
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default CustomerTagIndex;